import React, {createContext, useContext, useState, useEffect} from 'react';
import {fetchFileObjectsData} from '../utilities/fetchFileObjectsData';
import {AuthProvider, useAuth} from "./AuthContext";
import { usePersistedSelections } from '../hooks/usePersistedSelections';

// Re-export region utilities for convenience
export {
    REGIONS,
    getAccUrlForRegion,
    buildAssetUrl,
    buildFileUrl,
    getRegionByCode,
    getAllRegions,
    isValidRegion
} from '../utilities/regionUtils';

const GlobalContext = createContext({
    selectedHubId: null,
    setSelectedHubId: () => {
    },
    selectedHubRegion: null,
    setSelectedHubRegion: () => {
    },
    selectedCategoryId: null,
    setSelectedCategoryId: () => {
    },
    selectedProjectId: null,
    setSelectedProjectId: () => {
    },
    accTopFolder: null,
    setAccTopFolder: () => {
    },
    accCurrentNode: null,
    setAccCurrentNode: () => {
    },
    settingsFolderContent: null,
    setSettingsFolderContent: () => {
    },
    fileObjectsData: null,
    setFileObjectsData: () => {
    },
    selectedTagId: null,
    setSelectedTagId: () => {
    },
    selectedTagIdViewerLeft: null,
    setSelectedTagIdViewerLeft: () => {
    },
    selectedTagIdViewerRight: null,
    setSelectedTagIdViewerRight: () => {
    },
    selectedGroupData: null,
    setSelectedGroupData: () => {
    },
    selectedAssetId: null,
    setSelectedAssetId: () => {
    },
    loginStatus: null,
    setLoginStatus: () => {
    },
    refreshTreeTrigger: 0,
    triggerTreeRefresh: () => {
    },
    validateHubSelection: () => {
    },
    validateProjectSelection: () => {
    },
    clearAllSelections: () => {
    },
});

export const useGlobalProvider = () => useContext(GlobalContext);

const GlobalProviderInner = ({children}) => {
    // Use persistent selections hook for account and project selections
    const {
        selectedHubId,
        selectedHubRegion,
        selectedProjectId,
        selectedCategoryId,
        setSelectedHubId,
        setSelectedHubRegion,
        setSelectedProjectId,
        setSelectedCategoryId,
        validateHubSelection,
        validateProjectSelection,
        clearAllSelections
    } = usePersistedSelections();

    // Non-persistent state (session-only)
    const [accTopFolder, setAccTopFolder] = useState('');
    const [accCurrentNode, setAccCurrentNode] = useState('');
    const [settingsFolderContent, setSettingsFolderContent] = useState(null);
    const [fileObjectsData, setFileObjectsData] = useState(null);
    const [selectedAssetId, setSelectedAssetId] = useState('');
    const [selectedTagId, setSelectedTagId] = useState('');
    const [selectedTagIdViewerLeft, setSelectedTagIdViewerLeft] = useState('');
    const [selectedTagIdViewerRight, setSelectedTagIdViewerRight] = useState('');
    const [selectedGroupData, setSelectedGroupData] = useState(null);
    const [loginStatus, setLoginStatus] = useState('');
    const [refreshTreeTrigger, setRefreshTreeTrigger] = useState(0);

    const { getAccessToken, registerLoginStatusCallback } = useAuth();

    // Register the login status callback with AuthContext
    useEffect(() => {
        if (registerLoginStatusCallback) {
            registerLoginStatusCallback(setLoginStatus);
        }
    }, [registerLoginStatusCallback]);

    // Clear all selections when user logs out
    useEffect(() => {
        if (loginStatus === false) {
            console.log('User logged out, clearing all persisted selections');
            clearAllSelections();
        }
    }, [loginStatus, clearAllSelections]);

    // Log when selectedCategoryId changes
    useEffect(() => {
        console.log("Hub ID changed to:", selectedHubId);
    }, [selectedHubId]);

    useEffect(() => {
        console.log("Hub Region changed to:", selectedHubRegion);
    }, [selectedHubRegion]);

    useEffect(() => {
        console.log("Selected Category ID changed to:", selectedCategoryId, "type:", typeof selectedCategoryId);
    }, [selectedCategoryId]);

    // Log when selectedProjectId changes and reset selected file and asset
    useEffect(() => {
        console.log("Project ID changed to:", selectedProjectId);
        console.log("Project ID type:", typeof selectedProjectId);
        console.log("Project ID length:", selectedProjectId?.length);
        
        // Reset selected file and selected asset when project changes
        if (selectedProjectId) {
            console.log("Resetting selected file and asset due to project change");
            setAccCurrentNode('');
            setSelectedAssetId('');
            
            // Clear viewer URNs and tag IDs when project changes
            console.log("Clearing viewer URNs and tag IDs due to project change");
            setSelectedTagIdViewerLeft('');
            setSelectedTagIdViewerRight('');
            
            // Clear group context when project changes
            console.log("Clearing group context due to project change");
            setSelectedGroupData(null);
        }
    }, [selectedProjectId]);

    // Reset region when hub changes
    useEffect(() => {
        if (!selectedHubId) {
            setSelectedHubRegion('');
        }
    }, [selectedHubId]);

    // Log when selectedAssetId changes
    useEffect(() => {
        console.log("Asset ID changed to:", selectedAssetId);
    }, [selectedAssetId]);

    useEffect(() => {
        console.log("Login status:", loginStatus);
    }, [loginStatus]);

    // Log when accTopFolder changes
    useEffect(() => {
        console.log("accTopFolder changed to:", accTopFolder);
    }, [accTopFolder]);


    useEffect(() => {
        console.log("selected TAG id Viewer 1 in GlobalProvider:", selectedTagIdViewerLeft);
        setSelectedTagId(selectedTagIdViewerLeft);
    }, [selectedTagIdViewerLeft]);

    useEffect(() => {
        console.log("selected TAG id Viewer 2 in GlobalProvider:", selectedTagIdViewerRight);
        setSelectedTagId(selectedTagIdViewerRight);
    }, [selectedTagIdViewerRight]);

    useEffect(() => {
        setFileObjectsData(null);
    },[accCurrentNode]);

    const value = {
        selectedHubId,
        setSelectedHubId,
        selectedHubRegion,
        setSelectedHubRegion,
        selectedCategoryId,
        setSelectedCategoryId,
        selectedProjectId,
        setSelectedProjectId,
        accTopFolder,
        setAccTopFolder,
        accCurrentNode,
        setAccCurrentNode,
        settingsFolderContent,
        setSettingsFolderContent,
        selectedAssetId,
        setSelectedAssetId,
        selectedTagId,
        setSelectedTagId,
        selectedTagIdViewerLeft,
        setSelectedTagIdViewerLeft,
        selectedTagIdViewerRight,
        setSelectedTagIdViewerRight,
        selectedGroupData,
        setSelectedGroupData,
        loginStatus,
        setLoginStatus,
        fileObjectsData,
        setFileObjectsData: async () => {
            try {
                const token = await getAccessToken();
                await fetchFileObjectsData({
                    accAccessToken: token,
                    selectedProjectId,
                    accCurrentNode,
                    setFileObjectsData
                });
            } catch (error) {
                console.error('Error in setFileObjectsData:', error);
            }
        },
        refreshTreeTrigger,
        triggerTreeRefresh: () => {
            console.log('Triggering tree refresh...');
            setRefreshTreeTrigger(prev => prev + 1);
        },
        validateHubSelection,
        validateProjectSelection,
        clearAllSelections,
    };

    return (
        <GlobalContext.Provider value={value}>
            {children}
        </GlobalContext.Provider>
    );
};

export const GlobalProvider = ({children}) => {
    return (
        <GlobalProviderInner>
            {children}
        </GlobalProviderInner>
    );
};
